import { useEffect, useRef } from 'react'
import { MapPin, Users, Video, Heart, Briefcase } from 'lucide-react'
import { platformColors, platformIcons, platformNames } from '../data/config'
import { getTheme, isLightColor } from '../utils/colors'

function Hero({ config }) {
  const profile = config.profile
  const stats = config.stats
  const platforms = config.platforms
  const canvasRef = useRef(null)

  const fontColor = profile?.fontColor
  const isLightBg = isLightColor(profile?.sectionBackground || '#000000')
  const theme = getTheme(isLightBg, fontColor)

  // 背景设置
  const backgroundType = profile?.backgroundType || 'color'
  const backgroundColor = profile?.backgroundColor || '#000000'
  const backgroundImage = profile?.backgroundImage
  const backgroundEffect = profile?.backgroundEffect || 'particles'

  // 粒子背景动画
  useEffect(() => {
    if (backgroundEffect !== 'particles') return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    let animationId
    let particles = []

    // 从配置中读取强调色，转换为 RGB
    const accentColor = profile?.accentColor || '#8b5cf6'
    const hexToRgb = (hex) => {
      const h = hex.replace('#', '')
      return {
        r: parseInt(h.substring(0, 2), 16),
        g: parseInt(h.substring(2, 4), 16),
        b: parseInt(h.substring(4, 6), 16)
      }
    }
    const rgb = hexToRgb(accentColor)

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    // 创建粒子
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width
        this.y = Math.random() * canvas.height
        this.vx = (Math.random() - 0.5) * 0.5
        this.vy = (Math.random() - 0.5) * 0.5
        this.radius = Math.random() * 2 + 1
        this.color = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${Math.random() * 0.5 + 0.2})`
      }

      update() {
        this.x += this.vx
        this.y += this.vy

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1
      }

      draw() {
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2)
        ctx.fillStyle = this.color
        ctx.fill()
      }
    }

    // 初始化粒子
    for (let i = 0; i < 50; i++) {
      particles.push(new Particle())
    }

    // 动画循环
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // 绘制连线
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x
          const dy = p1.y - p2.y
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < 150) {
            ctx.beginPath()
            ctx.moveTo(p1.x, p1.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.2 * (1 - distance / 150)})`
            ctx.stroke()
          }
        })
      })

      // 更新和绘制粒子
      particles.forEach(particle => {
        particle.update()
        particle.draw()
      })

      animationId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resize)
      cancelAnimationFrame(animationId)
    }
  }, [backgroundEffect])

  // 平台列表
  const platformList = Object.entries(platforms).map(([key, data]) => ({
    key,
    ...data,
    color: platformColors[key],
    icon: platformIcons[key]
  }))

  // 动态背景样式
  const getBackgroundStyle = () => {
    if (backgroundType === 'image' && backgroundImage) {
      return {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    }
    return { backgroundColor: backgroundColor }
  }

  // 背景明亮度（0=最亮无遮罩, 100=最暗）
  const backgroundBrightness = profile?.backgroundBrightness ?? 30

  // 动态叠加层：明亮度滑块控制遮罩强度（0=最暗/最重遮罩, 100=最亮/几乎无遮罩）
  const getOverlayStyle = () => {
    // brightness=100 → factor≈0（几乎无遮罩），brightness=0 → factor=1（满遮罩）
    const factor = 1 - backgroundBrightness / 100

    if (backgroundType === 'image' && backgroundImage) {
      // 图片背景：user 可调范围 0.05(cr=100) ~ 0.60(cr=0)，足够宽
      const solidAlpha = (0.05 + factor * 0.55).toFixed(2)
      const topAlpha = (0.02 + factor * 0.38).toFixed(2)
      const bottomAlpha = (0.05 + factor * 0.65).toFixed(2)
      return {
        overlay: { backgroundColor: `rgba(0,0,0,${solidAlpha})` },
        gradient: { background: `linear-gradient(to bottom, rgba(0,0,0,${topAlpha}), transparent, rgba(0,0,0,${bottomAlpha}))` }
      }
    }
    // 纯色背景：factor 按比例缩放
    const topAlpha = (factor * 0.4).toFixed(2)
    const bottomAlpha = (factor * 0.7).toFixed(2)
    const solidAlpha = (factor * 0.5).toFixed(2)

    return {
      overlay: factor > 0 ? { backgroundColor: `rgba(0,0,0,${solidAlpha})` } : null,
      gradient: { background: `linear-gradient(to bottom, rgba(0,0,0,${topAlpha}), transparent, rgba(0,0,0,${bottomAlpha}))` }
    }
  }

  const overlayStyle = getOverlayStyle()

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={getBackgroundStyle()}
    >
      {/* 粒子背景 */}
      {backgroundEffect === 'particles' && <canvas ref={canvasRef} className="absolute inset-0 z-0" />}

      {/* 渐变流动背景效果 */}
      {backgroundEffect === 'gradient' && (
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/30 via-transparent to-purple-900/30 animate-pulse" />
          <div className="absolute inset-0 bg-gradient-to-tr from-accent/20 via-transparent to-pink-900/20" />
        </div>
      )}

      {/* 动态叠加层（明亮度可调：0=极亮，100=极暗） */}
      {overlayStyle.overlay && <div className="absolute inset-0 z-10" style={overlayStyle.overlay} />}
      <div className="absolute inset-0 z-10" style={overlayStyle.gradient} />

      {/* 内容 */}
      <div className="relative z-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          {/* 头像 */}
          <div className="mb-8">
            <div className="relative inline-block">
              <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-gradient-to-br from-accent to-accent-light p-1">
                <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center overflow-hidden">
                  {profile.avatar ? (
                    <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400" />
                  )}
                </div>
              </div>
              {/* 在线状态 */}
              <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 rounded-full border-4 border-black" />
            </div>
          </div>

          {/* 名称和标签 */}
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 ${theme.text}`}>
            {profile.name}
          </h1>
          <p className="text-xl sm:text-2xl text-accent mb-4">
            {profile.tagline}
          </p>

          {/* 领域标签 */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {profile.niche?.map((tag, index) => (
              <span
                key={index}
                className="px-4 py-1.5 rounded-full text-sm font-medium bg-accent/20 text-accent/70 border border-accent/30"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* 位置 */}
          <div className={`flex items-center justify-center gap-2 ${theme.textSecondary} mb-8`}>
            <MapPin className="w-4 h-4" />
            <span>{profile.location}</span>
          </div>

          {/* 汇总数据卡片 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-10">
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
              <Users className="w-6 h-6 text-accent mx-auto mb-2" />
                <div className={`text-2xl sm:text-3xl font-bold ${theme.text}`}>{stats.totalFollowers}</div>
              <div className={`text-sm ${theme.textSecondary}`}>总粉丝</div>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
              <Video className="w-6 h-6 text-pink-400 mx-auto mb-2" />
                <div className={`text-2xl sm:text-3xl font-bold ${theme.text}`}>{stats.totalWorks}</div>
              <div className={`text-sm ${theme.textSecondary}`}>作品数</div>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
              <Heart className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <div className={`text-2xl sm:text-3xl font-bold ${theme.text}`}>{stats.totalLikes}</div>
              <div className={`text-sm ${theme.textSecondary}`}>总获赞</div>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
              <Briefcase className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <div className={`text-2xl sm:text-3xl font-bold ${theme.text}`}>{stats.brandCollabs}</div>
              <div className={`text-sm ${theme.textSecondary}`}>品牌合作</div>
            </div>
          </div>

          {/* 各平台数据 */}
          <div className="max-w-4xl mx-auto">
            <h3 className={`text-lg ${theme.textSecondary} mb-4`}>各平台数据</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {platformList.map((platform) => (
                <a
                  key={platform.key}
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-all hover:scale-105"
                  style={{ borderColor: `${platform.color}30` }}
                >
                  <div className="text-2xl mb-2">{platform.icon}</div>
                  <div className={`font-semibold ${theme.text} mb-1`}>{platform.name}</div>
                  <div className={`text-lg font-bold`} style={{ color: platform.color }}>
                    {platform.followers}
                  </div>
                  <div className={`text-xs ${theme.textSecondary}`}>粉丝</div>
                </a>
              ))}
            </div>
          </div>

          {/* CTA 按钮 */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <a
              href="#works"
              className="px-8 py-3 bg-gradient-accent text-white font-semibold rounded-full hover:bg-gradient-accent transition-all transform hover:scale-105"
            >
              查看作品
            </a>
            <a
              href="#contact"
              className="px-8 py-3 border border-gray-600 text-white font-semibold rounded-full hover:border-accent hover:text-accent transition-all"
            >
              商务合作
            </a>
          </div>
        </div>
      </div>

      {/* 滚动提示 */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-gray-600 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-accent rounded-full" />
        </div>
      </div>
    </section>
  )
}

export default Hero
